import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { DirectoryContent } from "@/components/directory/DirectoryContent";
import { getDirectoryPageSize } from "@/utils/directoryQuery";
import { useDirectoryListings } from "@/hooks/useDirectoryListings";
import { useDirectoryDeepLinks } from "@/hooks/useDirectoryDeepLinks";
import { useDirectoryInteractions } from "@/hooks/useDirectoryInteractions";
import { useDirectorySupportData } from "@/hooks/useDirectorySupportData";

function hasAnyDirectoryFilter(filters) {
  const { activeCategory, detailsFilter, ...rest } = filters;
  return activeCategory !== "all" || detailsFilter !== "all" || Object.values(rest).some(Boolean);
}

export default function DirectoryPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeGrade, setActiveGrade] = useState(null);
  const [activeZone, setActiveZone] = useState(null);
  const [activeDomain, setActiveDomain] = useState(null);
  const [activeCounty, setActiveCounty] = useState(null);
  const [reviewModal, setReviewModal] = useState({ open: false, listingName: "" });
  const [symptomQuery, setSymptomQuery] = useState("");
  const [matchedDomains, setMatchedDomains] = useState([]);
  const [sortBy, setSortBy] = useState("default");
  const [clientVisible, setClientVisible] = useState(getDirectoryPageSize);
  const [userLocation, setUserLocation] = useState(null);
  const [locationSource, setLocationSource] = useState(null);
  const [geoStatus, setGeoStatus] = useState("idle");
  const [compareList, setCompareList] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [showFavsOnly, setShowFavsOnly] = useState(false);
  const [nearbyZip, setNearbyZip] = useState(null); // { zip, radius, cityLabel, lat, lng, cities: [] }
  const [nearbyRadius, setNearbyRadius] = useState(null); // 1 | 5 | 10 miles from userLocation
  const [detailsFilter, setDetailsFilter] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const setters = useMemo(() => ({
    setActiveCategory, setActiveCounty, setActiveDomain, setActiveGrade, setActiveZone,
    setClientVisible, setCompareList,
    setDetailsFilter, setGeoStatus, setLocationSource, setNearbyRadius, setNearbyZip, setSearchQuery,
    setMatchedDomains, setReviewModal, setSortBy, setSymptomQuery, setUserLocation,
  }), []);
  useDirectoryDeepLinks({ actions: setters, nearbyZip, searchParams, setSearchParams });
  const supportData = useDirectorySupportData();

  const queryPrimaryFilters = useMemo(() => ({
    activeCategory, activeDomain, activeGrade, detailsFilter, matchedDomains, searchQuery, showFavsOnly,
  }), [activeCategory, activeDomain, activeGrade, detailsFilter, matchedDomains, searchQuery, showFavsOnly]);
  const queryLocationFilters = useMemo(() => ({
    activeCounty, activeZone, nearbyRadius, nearbyZip, sortBy, userLocation,
  }), [activeCounty, activeZone, nearbyRadius, nearbyZip, sortBy, userLocation]);
  const queryFilters = useMemo(() => ({ ...queryPrimaryFilters, ...queryLocationFilters }), [queryLocationFilters, queryPrimaryFilters]);
  const { error, featured, hasMore, items, loadNextPage, loading, loadingMore, refreshing, searchRecovery, total } = useDirectoryListings({ queryFilters, searchQuery, showFavsOnly });

  useEffect(() => { setClientVisible(getDirectoryPageSize()); }, [queryFilters]);

  const displayItems = useMemo(
    () => (showFavsOnly ? items.slice(0, clientVisible) : items),
    [items, showFavsOnly, clientVisible]
  );
  const displayHasMore = showFavsOnly ? clientVisible < items.length : hasMore;
  const remainingCount = (showFavsOnly ? items.length : total) - displayItems.length;

  const hasActiveFilters = hasAnyDirectoryFilter({ activeCategory, activeCounty, activeDomain, activeGrade, activeZone, detailsFilter, nearbyRadius, nearbyZip, searchQuery });
  const interactionActions = useDirectoryInteractions({ loadNextPage, locationSource, searchParams, setSearchParams, setters, showFavsOnly, symptomQuery });

  const directoryFilters = useMemo(() => ({
    activeCategory, activeDomain, activeGrade, detailsFilter, matchedDomains,
  }), [activeCategory, activeDomain, activeGrade, detailsFilter, matchedDomains]);
  const directoryLocation = useMemo(() => ({
    activeCounty, activeZone, nearbyRadius, nearbyZip, searchQuery, showFavsOnly, sortBy,
  }), [activeCounty, activeZone, nearbyRadius, nearbyZip, searchQuery, showFavsOnly, sortBy]);
  const directoryResults = useMemo(() => ({
    displayHasMore, displayItems, featured, items, remainingCount, total,
  }), [displayHasMore, displayItems, featured, items, remainingCount, total]);
  const directoryStatus = useMemo(() => ({
    error, loading, loadingMore, refreshing, searchRecovery,
  }), [error, loading, loadingMore, refreshing, searchRecovery]);
  const directorySupport = useMemo(() => ({
    ...supportData,
  }), [supportData]);
  const directoryInteraction = useMemo(() => ({
    compareList, compareOpen, geoStatus, hasActiveFilters, reviewModal, symptomQuery, userLocation,
  }), [compareList, compareOpen, geoStatus, hasActiveFilters, reviewModal, symptomQuery, userLocation]);
  const directoryState = useMemo(() => ({
    ...directoryFilters, ...directoryInteraction, ...directoryLocation,
    ...directoryResults, ...directoryStatus, ...directorySupport,
  }), [directoryFilters, directoryInteraction, directoryLocation, directoryResults, directoryStatus, directorySupport]);
  const directoryActions = useMemo(() => ({
    ...interactionActions, setActiveCategory, setActiveCounty, setActiveDomain,
    setActiveGrade, setActiveZone, setCompareList, setCompareOpen, setDetailsFilter,
    setMatchedDomains, setNearbyRadius, setReviewModal, setSearchQuery, setShowFavsOnly,
    setSortBy, setSymptomQuery,
  }), [interactionActions]);

  return <DirectoryContent state={directoryState} actions={directoryActions} />;
}
